import { initializeApp } from 'firebase/app';
import {
  initializeFirestore,
  connectFirestoreEmulator,
  CACHE_SIZE_UNLIMITED,
} from 'firebase/firestore';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { getAnalytics } from 'firebase/analytics';
import { getMessaging, onMessage, type Messaging } from 'firebase/messaging';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';

declare global {
  interface Window {
    /** Read by the App Check SDK to issue debug tokens in development. */
    FIREBASE_APPCHECK_DEBUG_TOKEN?: boolean | string;
  }
}

// Not a secret. Firebase web config is public by design; access is controlled
// by security rules and App Check, not by hiding these values.
const firebaseConfig = {
  apiKey: 'AIzaSyAiGYJ5BzTfIjenQs3LwRqAzCOxxBJBJTc',
  authDomain: 'pun-and-done.firebaseapp.com',
  projectId: 'pun-and-done',
  storageBucket: 'pun-and-done.firebasestorage.app',
  messagingSenderId: '12318640885',
  appId: '1:12318640885:web:c6c30043f44a646708d3bc',
  measurementId: 'G-E5TDJDHK5F',
  databaseURL: 'https://pun-and-done.firebaseio.com',
};

const RECAPTCHA_SITE_KEY = '6Le16QUrAAAAAJGbscjtCPMbN17acXWv6KelTr6M';

const firebaseApp = initializeApp(firebaseConfig);

const isDevelopment = import.meta.env.DEV || window.location.hostname === 'localhost';

/**
 * Point Auth and Firestore at the local emulator suite instead of the real
 * project. Set by `npm run dev:local`; see README "Local development".
 *
 * Emulators do not enforce App Check, so it is skipped entirely in this mode —
 * no reCAPTCHA, no debug token, no Firebase console setup.
 */
const useEmulators = import.meta.env.VITE_USE_EMULATORS === 'true';

/* -------------------------------------------------------------------------- */
/* App Check                                                                   */
/* -------------------------------------------------------------------------- */

const DEBUG_TOKEN_STORAGE_KEY = 'pun_game_appcheck_debug_token';

/**
 * A stable App Check debug token for local development.
 *
 * reCAPTCHA cannot verify localhost, so App Check falls back to a debug token —
 * an arbitrary string registered in the Firebase console. App Check enforcement
 * is switched on for Firestore in this project, so localhost genuinely needs
 * one: without it every read fails with "Missing or insufficient permissions".
 *
 * The token is generated here rather than by the SDK, for two reasons. It is
 * kept in localStorage so it survives reloads and only has to be registered
 * once, and generating it means it can be logged plainly — the SDK logs its own
 * at warning level, where browser console filters routinely hide it.
 *
 * VITE_APPCHECK_DEBUG_TOKEN overrides it, which is useful for sharing one
 * registered token across browsers or machines.
 */
function getLocalDebugToken(): string {
  const configured = import.meta.env.VITE_APPCHECK_DEBUG_TOKEN;
  if (configured && configured !== 'true') {
    return configured;
  }

  const stored = window.localStorage.getItem(DEBUG_TOKEN_STORAGE_KEY);
  if (stored) {
    return stored;
  }

  const generated = crypto.randomUUID();
  window.localStorage.setItem(DEBUG_TOKEN_STORAGE_KEY, generated);
  return generated;
}

function setUpAppCheck(): void {
  try {
    // Kept from the original implementation: an empty container reCAPTCHA can
    // attach to. Harmless, and left in place because App Check failing in
    // production is not something that can be verified locally.
    if (typeof document !== 'undefined') {
      const recaptchaContainer = document.createElement('div');
      recaptchaContainer.id = 'recaptcha-container';
      recaptchaContainer.style.display = 'none';
      document.body.appendChild(recaptchaContainer);
    }

    if (isDevelopment) {
      const debugToken = getLocalDebugToken();
      window.FIREBASE_APPCHECK_DEBUG_TOKEN = debugToken;

      console.info(
        `%c[App Check] Local debug token:%c\n\n  ${debugToken}\n\n` +
          'If Firestore is returning "Missing or insufficient permissions", register it once:\n' +
          '  https://console.firebase.google.com/project/pun-and-done/appcheck/apps\n' +
          '  → your web app → ⋮ → Manage debug tokens → Add debug token → paste → save → reload.\n' +
          'It is stored in localStorage, so this only needs doing once per browser.\n' +
          'Or skip all of this and run `npm run dev:local` against the emulators.',
        'font-weight: bold',
        'font-weight: normal',
      );
    }

    initializeAppCheck(firebaseApp, {
      provider: new ReCaptchaV3Provider(RECAPTCHA_SITE_KEY),
      isTokenAutoRefreshEnabled: true,
    });
  } catch (error) {
    console.error('Failed to initialize App Check:', error);
  }
}

if (!useEmulators) {
  setUpAppCheck();
}

/* -------------------------------------------------------------------------- */
/* Services                                                                    */
/* -------------------------------------------------------------------------- */

const auth = getAuth(firebaseApp);

// Called for its side effect — page views are reported from here. Skipped
// against emulators so local runs do not pollute production analytics.
if (!useEmulators) {
  getAnalytics(firebaseApp);
}

const firestore = initializeFirestore(firebaseApp, {
  cacheSizeBytes: CACHE_SIZE_UNLIMITED,
  ignoreUndefinedProperties: true,
});

/** Every write to `puns` goes through callables here — see src/lib/api.ts. */
const functions = getFunctions(firebaseApp);

// Must happen before any read or write. firebase.ts is imported before any
// component runs, so this is the earliest possible point.
if (useEmulators) {
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  connectFirestoreEmulator(firestore, '127.0.0.1', 8080);
  connectFunctionsEmulator(functions, '127.0.0.1', 5001);

  console.info(
    '%c[Firebase] Local emulators%c — Auth :9099, Firestore :8080, UI http://127.0.0.1:4000\n' +
      'Production data is not being touched. Seeded accounts use password "password123".',
    'font-weight: bold',
    'font-weight: normal',
  );
}

// Offline persistence is NOT enabled. The previous code called the Firebase v8
// `db.enablePersistence()` on a v9 modular instance, which threw on every load
// into a swallowed catch — so the offline UI throughout the app has always been
// decorative. Enabling it properly means passing `localCache:
// persistentLocalCache(...)` to initializeFirestore above, which is a real
// behaviour change and wants testing of its own. See CLAUDE.md, gotcha 5.

/* -------------------------------------------------------------------------- */
/* Cloud Messaging                                                             */
/* -------------------------------------------------------------------------- */

let messaging: Messaging | undefined;

try {
  messaging = getMessaging(firebaseApp);
} catch {
  console.log('Firebase messaging is not supported in this browser');
}

/**
 * The same notification can arrive more than once (multiple tabs, or both the
 * page and the service worker reacting). Recently shown notifications are
 * fingerprinted by title+body and suppressed for a few seconds.
 */
const NOTIFICATION_DEDUPE_WINDOW_MS = 5000;
const recentNotifications = new Set<string>();

if (messaging) {
  onMessage(messaging, (payload) => {
    const title = payload.data?.title ?? '';
    const body = payload.data?.body ?? '';
    const fingerprint = `${title}|${body}`;

    if (recentNotifications.has(fingerprint)) {
      return;
    }

    recentNotifications.add(fingerprint);
    setTimeout(() => recentNotifications.delete(fingerprint), NOTIFICATION_DEDUPE_WINDOW_MS);

    // Daily reminders are left to the service worker, which shows them whether
    // or not the page is focused. Friend notifications are shown here so they
    // still surface briefly while the app is open.
    if (payload.data?.type !== 'friend_pun') {
      return;
    }

    if (Notification.permission !== 'granted' || !title) {
      return;
    }

    // Chrome for Android throws "Illegal constructor" on `new Notification(...)`
    // called from page script — it only allows notifications created through a
    // service worker. Routing through the SW here, the same as the background
    // path below, also means clicks go through the SW's notificationclick
    // handler for free, instead of the bare `window.focus()` a page-context
    // notification.onclick was limited to.
    const tag = `friend-pun-${payload.data.punId}`;
    navigator.serviceWorker.ready.then(async (registration) => {
      await registration.showNotification(title, {
        body,
        icon: '/logo192.png',
        tag,
        requireInteraction: false,
        silent: false,
        data: payload.data,
      });

      setTimeout(async () => {
        const shown = await registration.getNotifications({ tag });
        shown.forEach((n) => n.close());
      }, 4000);
    });
  });
}

export { firestore, auth, functions, messaging };
